import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { newWayCard, campaign, hero, tickerItems } from '../mock';
import { Play, ArrowRight, X, BookOpen, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { FadeUp } from './MotionReveals';

export function NewWayCard() {
  const [show, setShow] = useState(true);
  if (!show) return null;
  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-6">
      <div className="relative rounded-2xl bg-gradient-to-br from-[#161618] to-[#0d0d0e] border border-white/10 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-red-600/10 blur-3xl" />
        <div className="p-5 lg:p-6 flex flex-col md:flex-row md:items-center gap-5 relative">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/5 border border-white/10 shrink-0 mt-4 md:mt-0">
            <BookOpen className="text-red-400" size={22} />
          </div>
          <div className="flex-1 mt-2 md:mt-0">
            <h3 className="font-serif-editorial text-[26px] lg:text-[32px] text-white leading-tight">{newWayCard.title}</h3>
            <p className="text-white/70 text-[14px] lg:text-[15px] mt-1 max-w-2xl">
              Découvre nos <strong className="text-white">31 enquêtes magazine</strong> + <strong className="text-white">9 dossiers ETG</strong> : témoignages, statistiques, cas réels — sans chrono, sans score. Tu apprends en lisant, à ton rythme.
            </p>
            <div className="mt-2 text-[12px] text-white/40 font-mono-spec">{newWayCard.stats}</div>
          </div>
          <Link
            to="/magazine"
            data-testid="home-newway-cta"
            className="shrink-0 inline-flex items-center gap-2 px-5 h-11 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold transition shadow-lg shadow-red-900/40"
          >
            {newWayCard.cta} <ArrowRight size={15} />
          </Link>
          <button onClick={() => setShow(false)} className="absolute top-3 right-3 text-white/40 hover:text-white"><X size={16} /></button>
        </div>
      </div>
    </div>
  );
}

export function CampaignBlock() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 pt-6">
      <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d0e] grid md:grid-cols-2">
        <div className="p-7 lg:p-9 relative flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600/15 text-red-400 text-[11px] font-semibold uppercase tracking-wider border border-red-600/30">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> {campaign.badge}
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-medium">{campaign.category}</span>
          </div>
          <h2 className="font-serif-editorial text-white text-[24px] sm:text-[32px] lg:text-[44px] leading-[1.06] sm:leading-[1.04] tracking-tight">{campaign.title}</h2>
          <p className="mt-4 text-white/70 text-[15px] leading-relaxed max-w-md">{campaign.description}</p>
          <div className="mt-6 flex items-center gap-3 text-white/50 text-[12px] font-mono-spec">
            <span>{campaign.duration}</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>{campaign.decryptage}</span>
          </div>
          <div className="mt-6 flex gap-3 flex-wrap">
            <Link
              to="/magazine/fatigue-au-volant-le-tueur-silencieux"
              data-testid="home-campaign-primary"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-black text-[14px] font-semibold hover:bg-white/90 transition"
            >
              {campaign.primaryCta} <ArrowRight size={15} />
            </Link>
            <a
              href="https://www.youtube.com/results?search_query=s%C3%A9curit%C3%A9+routi%C3%A8re+film+officiel+2026"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-campaign-secondary"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 text-white text-[14px] font-medium hover:border-white/40 hover:bg-white/5 transition"
            >
              <Play size={14} /> {campaign.secondaryCta}
            </a>
          </div>
        </div>
        <div className="relative min-h-[300px] md:min-h-full group cursor-pointer">
          <img src={campaign.videoThumb} alt={campaign.title} className="absolute inset-0 w-full h-full object-cover img-zoom" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/20 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
              <Play className="text-black ml-1" size={26} fill="currentColor" />
            </div>
          </div>
          <div className="absolute bottom-4 left-4 right-4 text-white text-[11px] font-mono-spec opacity-80">
            <div>{campaign.credits}</div>
            <div className="text-white/60">{campaign.director}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HeroSection() {
  const reduce = useReducedMotion();
  // Pexels videos verified HTTP 206 — drone aerial highway loop.
  // Poster (image) is used if video can't load (slow conn / Save-Data).
  const VIDEO_HD = 'https://videos.pexels.com/video-files/2103099/2103099-hd_1920_1080_30fps.mp4';
  const VIDEO_SD = 'https://videos.pexels.com/video-files/2103099/2103099-sd_640_360_30fps.mp4';
  return (
    <section className="relative mt-10 lg:mt-14">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden min-h-[380px] sm:min-h-[480px] lg:min-h-[640px] flex items-end">
          {/* Background video — Pexels drone aerial highway, autoplay loop muted */}
          {!reduce ? (
            <video
              data-testid="hero-bg-video"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              poster={hero.bgImage}
              className="absolute inset-0 w-full h-full object-cover"
              aria-hidden="true"
            >
              <source src={VIDEO_HD} type="video/mp4" media="(min-width: 768px)" />
              <source src={VIDEO_SD} type="video/mp4" />
            </video>
          ) : (
            <img src={hero.bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/65 to-black/35" />
          <div className="absolute inset-0 grid-lines opacity-30" />
          {/* Subtle vignette pulse on red accent */}
          <motion.div
            className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-red-600/10 blur-3xl pointer-events-none"
            animate={reduce ? {} : { opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="relative z-10 p-5 sm:p-7 lg:p-12 w-full">
            <FadeUp delay={0.05} y={14}>
              <span className="inline-block text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.25em] text-red-400 font-semibold mb-3 sm:mb-5">{hero.badge}</span>
            </FadeUp>
            <FadeUp delay={0.15}>
              <h1 className="font-serif-editorial text-white text-[30px] sm:text-[52px] lg:text-[92px] leading-[0.98] tracking-tight max-w-5xl text-shadow-strong">
                {hero.title[0]} <span className="text-white/70">{hero.title[1]}</span><br />
                {hero.title[2]} <span className="text-red-500">{hero.title[3]}</span>
              </h1>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-5 sm:mt-8 flex flex-wrap gap-2 sm:gap-3">
                <Link
                  to="/eleve"
                  data-testid="home-hero-primary"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold transition shadow-xl shadow-red-900/40 hover:scale-[1.03] active:scale-95"
                >
                  {hero.primaryCta} <ArrowRight size={15} />
                </Link>
                <Link
                  to="/magazine"
                  data-testid="home-hero-secondary"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 bg-white/5 backdrop-blur text-white text-[14px] font-medium hover:bg-white/10 transition hover:scale-[1.03] active:scale-95"
                >
                  {hero.secondaryCta}
                </Link>
              </div>
            </FadeUp>
            <FadeUp delay={0.45}>
              <div className="mt-6 text-white/60 text-[12.5px] max-w-xl">
                {hero.tagline}<span className="text-white/40"> {hero.free}</span>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Ticker() {
  const items = [...tickerItems, ...tickerItems, ...tickerItems];
  return (
    <div className="mt-10 border-y border-white/10 bg-[#0d0d0e] overflow-hidden">
      <div className="flex animate-marquee py-4 whitespace-nowrap">
        {items.map((t, i) => (
          <div key={i} className="flex items-center gap-4 px-6 text-white/80 text-[14px] font-medium">
            <span>{t}</span>
            <span className="w-1 h-1 rounded-full bg-red-500" />
          </div>
        ))}
      </div>
    </div>
  );
}
